import { Fragment, ReactNode } from 'react';

import { Link, makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import { useRouteRef } from '@backstage/core-plugin-api';

import {
  extractLastNameComponent,
  extractWorkflowFromInstanceName,
} from '@spotify/confidence-core-react';
import { getTypeOrNull } from '@spotify/confidence-graphql';
import {
  ActivityChangeOptions,
  ActivityItem,
} from '@spotify/plugin-confidence-activity-react';
import {
  FlagUpdatesRuleDetails,
  createFlagUpdatesChangeExtension,
} from '@spotify/plugin-confidence-flags-react';
import {
  useGetWorkflowInstance,
  workflowInstanceRouteRef,
} from '@spotify/plugin-confidence-workflows-react';

const useStyles = makeStyles({
  addedText: {
    color: 'var(--essential-positive)',
  },
  removedText: {
    color: 'var(--text-subdued)',
    textDecoration: 'line-through',
  },
});

const ChangeRender = ({ change }: ActivityChangeOptions) => {
  const classes = useStyles();

  const oldValue = change.oldValue;
  const newValue = change.newValue;

  const oldName = oldValue?.labels?.workflowInstance;
  const newName = newValue?.labels?.workflowInstance;

  const { data: oldData } = useGetWorkflowInstance({
    workflowId: extractWorkflowFromInstanceName(oldName) ?? '',
    instanceId: extractLastNameComponent(oldName) ?? '',
    skip: !oldName,
  });

  const { data: newData } = useGetWorkflowInstance({
    workflowId: extractWorkflowFromInstanceName(newName) ?? '',
    instanceId: extractLastNameComponent(newName) ?? '',
    skip: !newName,
  });

  const oldDisplayName =
    getTypeOrNull(oldData?.workflowInstance)?.displayName ??
    extractLastNameComponent(oldName);
  const newDisplayName =
    getTypeOrNull(newData?.workflowInstance)?.displayName ??
    extractLastNameComponent(newName);

  const workflowInstanceRoute = useRouteRef(workflowInstanceRouteRef);

  let content: ReactNode = null;

  if (oldName && newName) {
    // Reference to workflow instance exist in both old and new value
    if (oldDisplayName !== newDisplayName) {
      content = (
        <Fragment>
          <Link
            className={classes.removedText}
            href={workflowInstanceRoute({
              workflow: extractWorkflowFromInstanceName(oldName) ?? '',
              id: extractLastNameComponent(oldName) ?? '',
            })}
           data-track-source-anchor="0941a4439f;change-render-link">
            {oldDisplayName}{' '}
          </Link>{' '}
          <Link
            className={classes.addedText}
            href={workflowInstanceRoute({
              workflow: extractWorkflowFromInstanceName(newName) ?? '',
              id: extractLastNameComponent(newName) ?? '',
            })}
           data-track-source-anchor="eaa347e0e4;change-render-link-2">
            {newDisplayName}{' '}
          </Link>
        </Fragment>
      );
    } else {
      content = (
        <Link
          href={workflowInstanceRoute({
            workflow: extractWorkflowFromInstanceName(newName) ?? '',
            id: extractLastNameComponent(newName) ?? '',
          })}
         data-track-source-anchor="2fba3fe210;change-render-link-3">
          {newDisplayName}{' '}
        </Link>
      );
    }
  } else if (oldName) {
    // Reference to workflow instance exist in old value but not in new value.
    // Detached the workflow instance from the flag.
    content = (
      <Link
        className={classes.removedText}
        href={workflowInstanceRoute({
          workflow: extractWorkflowFromInstanceName(oldName) ?? '',
          id: extractLastNameComponent(oldName) ?? '',
        })}
       data-track-source-anchor="e0ec26cedc;change-render-link-4">
        {oldDisplayName}{' '}
      </Link>
    );
  } else if (newName) {
    // Reference to workflow instance exist in new value but not in old value.
    // Attached the workflow instance to the flag.
    content = (
      <Link
        className={classNames({ [classes.addedText]: Boolean(oldValue) })}
        href={workflowInstanceRoute({
          workflow: extractWorkflowFromInstanceName(newName) ?? '',
          id: extractLastNameComponent(newName) ?? '',
        })}
       data-track-source-anchor="d01617d43d;change-render-link-5">
        {newDisplayName}{' '}
      </Link>
    );
  }

  return (
    <Fragment>
      <ActivityItem.Section label="Workflow Instance">
        {content}
      </ActivityItem.Section>
      <FlagUpdatesRuleDetails
        oldRule={change.oldValue}
        newRule={change.newValue}
      />
    </Fragment>
  );
};

export const WORKFLOW_INSTANCE_RULE_CHANGE_EXTENSION =
  createFlagUpdatesChangeExtension({
    id: 'workflow-instance-rule-change',
    priority: 10,
    predicate: ({ change }) =>
      change.fieldType === 'confidence.flags.admin.v1.Flag.Rule' &&
      (change.oldValue?.labels?.workflowInstance ||
        change.newValue?.labels?.workflowInstance),

    title: change => {
      const oldValue = change.oldValue;
      const oldName = oldValue?.labels?.workflowInstance;
      const oldEnabled = oldValue?.enabled;

      const newValue = change.newValue;
      const newName = newValue?.labels?.workflowInstance;
      const newEnabled = newValue?.enabled;

      if (oldValue && newValue) {
        // A change to the rule.

        if (oldName && newName && oldName !== newName) {
          // Switched workflow instance.
          return 'Replaced workflow instance';
        }

        if (oldEnabled !== newEnabled) {
          if (newEnabled) {
            return 'Launched workflow instance';
          }

          return 'Ended workflow instance';
        }
      } else if (newValue) {
        // A new rule was created with a workflow instance attached.
        return `Created rule #${extractLastNameComponent(newName)?.slice(-4)} for workflow instance`;
      }

      return change.description ?? 'Workflow instance rule changed';
    },

    render: ChangeRender,
  });
